import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { SortModule } from "tabulator-tables";
import { Tabulator, FormatModule, DownloadModule, PageModule, ExportModule, RowComponent } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import "tabulator-tables/dist/css/tabulator_bootstrap5.min.css";
import { EmployeeData } from "types/employee";
import * as XLSX from "xlsx";

type EmployeeDataProps = {
  data: EmployeeData[];
};

(window as any).XLSX = XLSX;

Tabulator.registerModule([FormatModule, DownloadModule, PageModule, ExportModule, SortModule]);

const EmployeeTable = forwardRef<InstanceType<typeof Tabulator>, EmployeeDataProps>(
  ({ data }, ref) => {
    const [isMobile, setIsMobile] = useState(false);
    const tableRef = useRef<HTMLDivElement>(null);
    const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);

    useImperativeHandle(ref, () => tabulatorRef.current, [tabulatorRef.current]);

    useEffect(() => {
      const mobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) &&
        window.innerWidth < 1025;

      setIsMobile(mobile);

      if (tableRef.current) {
        tableRef.current.innerHTML = "";

        tabulatorRef.current = new Tabulator(tableRef.current, {
          data,
          layout: isMobile ? "fitDataFill" : "fitColumns",
          pagination: "local",
          paginationSize: 10,
          paginationSizeSelector: [10, 25, 50, 100],
          theme: "bootstrap5",
          headerSort: true,
          initialSort: [
            {
              column: "fiscal_year",
              dir: "desc",
            }
          ],
          rowFormatter: (row: typeof RowComponent) => {
            row.getElement().classList.add("table-striped");
          },
          columns: [
            { 
              titleFormatter: () => {
                const element = document.createElement("span");
                element.innerHTML = "<i class='fas fa-calendar-alt'></i> Year";
                return element;
              }, 
              field: "fiscal_year", 
              titleDownload: "Fiscal Year", 
              headerSort: true 
            },
            { 
              titleFormatter: () => {
                const element = document.createElement("span");
                element.innerHTML = "<i class='fas fa-landmark'></i> Entity";
                return element
              },
              field: "entity_name", 
              titleDownload: "Entity", 
              headerSort: true 
            },
            { 
              titleFormatter: () => {
                const element = document.createElement("span");
                element.innerHTML = "<i class='fas fa-user'></i> Employee";
                return element;
              },
              field: "employee_name", 
              titleDownload: "Employee", 
              headerSort: true 
            },
            { 
              titleFormatter: () => {
                const element = document.createElement("span");
                element.innerHTML = "<i class='fas fa-briefcase'></i> Title";
                return element;
              },
              field: "title", 
              titleDownload: "Title", 
              headerSort: true,
              formatter: "textarea",
            },
            {
              titleFormatter: () => {
                const element = document.createElement("span");
                element.innerHTML = "<i class='fas fa-dollar-sign'></i> Wages";
                return element;
              },
              field: "wages",
              formatter: "money",
              sorter: "number",
              titleDownload: "Wages",
              formatterParams: { decimal: ".", thousand: ",", symbol: "$", precision: 2 },
            },
            {
              titleFormatter: () => {
                const element = document.createElement("span");
                element.innerHTML = "<i class='fas fa-dollar-sign'></i> Benefits";
                return element;
              },
              field: "benefits",
              formatter: "money",
              sorter: "number",
              titleDownload: "Benefits",
              formatterParams: { decimal: ".", thousand: ",", symbol: "$", precision: 2 },
            },
            {
              titleFormatter: () => {
                const element = document.createElement("span");
                element.innerHTML = "<i class='fas fa-dollar-sign'></i> Total";
                return element;
              },
              field: "total",
              formatter: "money",
              sorter: "number",
              titleDownload: "Total",
              formatterParams: { decimal: ".", thousand: ",", symbol: "$", precision: 2 },
            },
          ],
        });
      }
    }, [data]);

    return <div ref={tableRef} id="tabulator_table" />;
  }
);

EmployeeTable.displayName = "EmployeeTable";

export default EmployeeTable;